import React, { useState, useEffect, useRef, useCallback } from "react";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import { Mic, MicOff } from "lucide-react";
import _, { set } from "lodash";
import { getTempTokenAzureSTT } from "../../requests";
import { unlockAudioPlayback } from "../../utils/audioPlaybackManager";
import { getMicAudioConstraints } from "../../utils/deviceDetection";
import {
  acquireMicStream,
  releaseMicStream,
} from "../../utils/micStreamManager";
import {
  isArabicSpeechLanguage,
  looksLikeArabic,
} from "../helper/formatters";

const ARABIC_STT_MIN_CONFIDENCE = 0.6;

const getAzureUtteranceConfidence = (result) => {
  try {
    const parsed = JSON.parse(result?.json || "{}");
    const confidence = parsed?.NBest?.[0]?.Confidence;
    return typeof confidence === "number" ? confidence : null;
  } catch {
    return null;
  }
};

const shouldDropArabicUtterance = (text, result, language) => {
  if (!isArabicSpeechLanguage(language)) return false;

  const confidence = getAzureUtteranceConfidence(result);
  if (confidence !== null && confidence < ARABIC_STT_MIN_CONFIDENCE) {
    return true;
  }

  return !looksLikeArabic(text);
};

const SpeechToText = ({
  handleQuery,
  isDisabled,
  language = "en-US",
  isQueryLockedProp,
  aiAudioCompleted,
  styles,
}) => {
  const [isMicEnabled, setIsMicEnabled] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [finalTranscription, setFinalTranscription] = useState("");
  const [audioChunks, setAudioChunks] = useState([]);
  const [firstRender, setFirstRender] = useState(true);
  const [isAutomaticStop, setAutomaticStop] = useState(false);
  const [shouldAutoEnable, setShouldAutoEnable] = useState(false);
  const [speechToken, setSpeechToken] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isMicLoading, setIsMicLoading] = useState(false);

  // Mount tracking ref
  const isMountedRef = useRef(true);

  // Refs for resources
  const mediaRecorder = useRef(null);
  const speechRecognizer = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // Timeout refs
  const silenceTimeout = useRef(null);
  const recognitionTimeout = useRef(null);
  const autoEnableTimeout = useRef(null);
  const tokenRefreshTimeout = useRef(null);

  // State refs for synchronization
  const isQueryLocked = useRef(false);
  const isCurrentlyProcessing = useRef(false);
  const hasBeenDisabled = useRef(false);
  const lastMessageWasVoice = useRef(false);
  const hasUserEnabledMicBefore = useRef(false);
  const pendingAutoEnable = useRef(false);

  // Configuration refs
  const speechConfig = useRef(null);
  const audioConfig = useRef(null);
  const animationLines = useRef([0, 0, 0]);
  const finalTranscriptRef = useRef("");

  const speechRegion = process.env.REACT_APP_AZURE_SPEECH_REGION;

  // Remove useCallback entirely - it's causing stale closures
  const fetchSpeechToken = async () => {
    try {
      const response = await getTempTokenAzureSTT();
      if (response.status === 200) {
        if (tokenRefreshTimeout.current) {
          clearTimeout(tokenRefreshTimeout.current);
        }
        tokenRefreshTimeout.current = setTimeout(() => {
          if (isMountedRef.current) {
            console.log("🔄 Refreshing token - component still mounted");
            fetchSpeechToken(); // ✅ Now this will work
          } else {
            console.warn("🚫 Skipping token refresh - component unmounted");
          }
        }, 9 * 60 * 1000);

        setSpeechToken(response.data.token);
        console.log("✅ Speech token refreshed successfully");
      } else {
        console.error("Failed to fetch speech token:", response.data);
      }
    } catch (error) {
      console.error("Error fetching speech token:", error);
    }
  };

  useEffect(() => {
    fetchSpeechToken();

    // Cleanup token refresh on unmount
    return () => {
      if (tokenRefreshTimeout.current) {
        clearTimeout(tokenRefreshTimeout.current);
        tokenRefreshTimeout.current = null;
      }
    };
  }, []);

  // Initialize speech configuration
  useEffect(() => {
    if (!speechToken || !speechRegion) {
      console.log("🚫: Azure Speech token or region is not configured.");
      return;
    }

    speechConfig.current = sdk.SpeechConfig.fromAuthorizationToken(
      speechToken,
      speechRegion
    );

    // var speechLang = "en-US";
    // if (_.lowerCase(language) === "english") {
    //   speechLang = "en-US";
    // } else if (_.lowerCase(language) === "dutch") {
    //   speechLang = "nl-NL";
    // }

    speechConfig.current.speechRecognitionLanguage = language;
    speechConfig.current.outputFormat = sdk.OutputFormat.Detailed;
    speechConfig.current.requestWordLevelTimestamps();
    speechConfig.current.enableDictation();

    // it was causing issue with token refresh as it was clearing the resources on speechtoken refresh.
    // return () => {
    //   cleanupAllResources();
    // };
  }, [language, speechToken, speechRegion]);

  // Handle disable state changes - ONLY for disabling mic
  useEffect(() => {
    if (typeof isDisabled === "boolean" && !firstRender) {
      if (isDisabled && isMicEnabled) {
        console.log("🛑 Disabling mic due to isDisabled=true");
        // Immediately stop and cleanup when disabled
        hasBeenDisabled.current = true;
        pendingAutoEnable.current = false;
        cleanupAllResources();
        setIsMicEnabled(false);
        setIsRecording(false);
        setTranscription("");
        setFinalTranscription("");
        setAudioChunks([]);
        finalTranscriptRef.current = "";
      }

      // If becoming disabled and last message was voice, set pending auto-enable
      if (
        isDisabled &&
        lastMessageWasVoice.current &&
        hasUserEnabledMicBefore.current
      ) {
        pendingAutoEnable.current = true;
      }
    }
    setFirstRender(false);
  }, [isDisabled]);

  // Auto-enable logic - similar to LiveTranscribe
  useEffect(() => {
    // Don't auto-enable on initial load
    if (!hasInitialized) {
      setHasInitialized(true);
      return;
    }

    if (
      aiAudioCompleted &&
      !isDisabled &&
      !isCurrentlyProcessing.current &&
      !isMicEnabled
    ) {
      console.log("✅ Auto-enabling mic after AI response complete");
      // Actually start recording by calling handleMicButtonClick
      handleMicButtonClick(false);
      setShouldAutoEnable(false);
      pendingAutoEnable.current = false;
      hasBeenDisabled.current = false;
      setAutomaticStop(false);
    }
  }, [aiAudioCompleted, isDisabled]);

  // Legacy auto-enable logic for backward compatibility
  useEffect(() => {
    if (
      aiAudioCompleted &&
      pendingAutoEnable.current &&
      !isDisabled &&
      !isCurrentlyProcessing.current
    ) {
      pendingAutoEnable.current = false;
      hasBeenDisabled.current = false;
      setAutomaticStop(false);

      autoEnableTimeout.current = setTimeout(() => {
        if (!isDisabled && !isCurrentlyProcessing.current) {
          setShouldAutoEnable(true);
        }
      }, 300);
    }
  }, [aiAudioCompleted, isDisabled]);

  useEffect(() => {
    handleQueryLock(isQueryLockedProp);
  }, [isQueryLockedProp]);

  // Comprehensive cleanup on unmount
  useEffect(() => {
    return () => {
      // Mark component as unmounted to prevent token refresh
      isMountedRef.current = false;

      cleanupAllResources();
      // Extra cleanup for token refresh
      if (tokenRefreshTimeout.current) {
        clearTimeout(tokenRefreshTimeout.current);
        tokenRefreshTimeout.current = null;
      }
    };
  }, []);

  const handleQueryLock = (status) => {
    isQueryLocked.current = status;
  };

  // Comprehensive cleanup function
  const cleanupAllResources = useCallback(async () => {
    // Clear all timeouts
    if (silenceTimeout.current) {
      clearTimeout(silenceTimeout.current);
      silenceTimeout.current = null;
    }
    if (recognitionTimeout.current) {
      clearTimeout(recognitionTimeout.current);
      recognitionTimeout.current = null;
    }
    if (autoEnableTimeout.current) {
      clearTimeout(autoEnableTimeout.current);
      autoEnableTimeout.current = null;
    }
    // if (tokenRefreshTimeout.current) {
    //   clearTimeout(tokenRefreshTimeout.current);
    //   tokenRefreshTimeout.current = null;
    // }

    // Stop animation
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    // Stop speech recognition
    if (speechRecognizer.current) {
      try {
        await speechRecognizer.current.stopContinuousRecognitionAsync(
          () => {},
          (err) => console.warn("Error stopping speech recognition:", err)
        );
        speechRecognizer.current.close();
      } catch (e) {
        console.warn("Error closing SpeechRecognizer:", e);
      }
      speechRecognizer.current = null;
    }

    // Stop media recorder
    if (mediaRecorder.current && mediaRecorder.current.state === "recording") {
      try {
        mediaRecorder.current.stop();
      } catch (e) {
        console.warn("Error stopping MediaRecorder:", e);
      }
      mediaRecorder.current = null;
    }

    // Release shared mic stream so iOS can leave playAndRecord during playback
    if (mediaStreamRef.current) {
      releaseMicStream();
      mediaStreamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try {
        await audioContextRef.current.close();
      } catch (e) {
        console.warn("Error closing AudioContext:", e);
      }
      audioContextRef.current = null;
      analyserRef.current = null;
    }

    // Reset processing state
    isCurrentlyProcessing.current = false;
  }, []);

  const analyzeAudio = useCallback(() => {
    if (!analyserRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    const bufferLength = analyserRef.current.fftSize;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationFrameId.current = requestAnimationFrame(draw);

      analyserRef.current.getByteTimeDomainData(dataArray);

      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);

      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += Math.abs(dataArray[i] - 128);
      }
      const averageAmplitude = sum / bufferLength;
      const normalizedAmplitude = averageAmplitude;

      const lineHeightMultiplier = normalizedAmplitude * canvas.height * 0.8;

      const numLines = animationLines.current.length;
      const lineWidth = canvas.width / (numLines * 2 + 2);
      const spacing = lineWidth;
      const centerY = canvas.height / 2;

      if (normalizedAmplitude < 0.05) {
        for (let i = 0; i < numLines; i++) {
          const x = spacing + i * (lineWidth + spacing) + lineWidth / 2;
          canvasCtx.fillStyle = "skyblue";
          canvasCtx.beginPath();
          canvasCtx.arc(x, centerY, lineWidth / 4, 0, Math.PI * 2);
          canvasCtx.fill();
        }
      } else {
        for (let i = 0; i < numLines; i++) {
          const x = spacing + i * (lineWidth + spacing);
          const height =
            lineHeightMultiplier *
            (0.7 + Math.sin(Date.now() * 0.003 + (i * Math.PI) / 1.5) * 0.3);
          const y = centerY - height / 2;
          canvasCtx.fillStyle = "skyblue";
          canvasCtx.fillRect(x, y, lineWidth, height);
        }
      }
    };

    draw();
  }, []);

  const handleMicButtonClick = useCallback(
    async (mikeState) => {
      console.log("🎤 Mic button clicked:", {
        mikeState,
        isDisabled,
        isCurrentlyProcessing: isCurrentlyProcessing.current,
        isMicEnabled,
      });

      // Prevent multiple simultaneous operations
      if (isCurrentlyProcessing.current) {
        console.log("⚠️ Currently processing, ignoring mic click");
        return;
      }

      if (!mikeState) {
        // Starting recording
        unlockAudioPlayback();
        console.log("mic is starting and isDisabled: ", isDisabled);
        if (isDisabled) {
          return;
        }

        hasUserEnabledMicBefore.current = true;
        isCurrentlyProcessing.current = true;
        setIsMicLoading(true); // 🔵 start loading animation

        try {
          // Clear any previous state
          setTranscription("");
          setFinalTranscription("");
          setAudioChunks([]);
          finalTranscriptRef.current = "";

          // Clear any existing timeouts
          if (silenceTimeout.current) {
            clearTimeout(silenceTimeout.current);
            silenceTimeout.current = null;
          }
          if (recognitionTimeout.current) {
            clearTimeout(recognitionTimeout.current);
            recognitionTimeout.current = null;
          }

          const stream = await acquireMicStream(getMicAudioConstraints());

          mediaStreamRef.current = stream;

          // Start audio recording
          startAudioRecording(stream);
          setIsRecording(true);
          setIsMicEnabled(true);

          // Configure Azure Speech Recognition
          audioConfig.current = sdk.AudioConfig.fromStreamInput(stream);
          speechRecognizer.current = new sdk.SpeechRecognizer(
            speechConfig.current,
            audioConfig.current
          );

          // Set up Web Audio Analyser for visualization
          audioContextRef.current = new (window.AudioContext ||
            window.webkitAudioContext)();

          if (audioContextRef.current.state === "suspended") {
            audioContextRef.current.resume().then(() => {});
          }

          const source =
            audioContextRef.current.createMediaStreamSource(stream);
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.fftSize = 2048;
          source.connect(analyserRef.current);

          setTimeout(() => {
            if (canvasRef.current && analyserRef.current) {
              analyzeAudio();
            }
          }, 200);

          speechRecognizer.current.recognizing = (s, event) => {
            console.log("🎤 RECOGNIZING fired - text:", event.result.text);

            // Clear timeout FIRST - as long as this fires, user is speaking
            if (silenceTimeout.current) {
              console.log("✅ CLEARING silence timeout - user still speaking");
              clearTimeout(silenceTimeout.current);
              silenceTimeout.current = null;
            }

            if (isDisabled || isQueryLocked.current) {
              console.log("⚠️ Skipping - disabled or locked");
              return;
            }

            const currentText = event.result.text;
            if (currentText && currentText.trim()) {
              setTranscription(currentText);
            }
          };

          speechRecognizer.current.recognized = (s, event) => {
            console.log("✓ RECOGNIZED fired - reason:", event.result.reason);

            if (isDisabled || isQueryLocked.current) {
              console.log(
                "⚠️ Skipping recognized - disabled:",
                isDisabled,
                " or locked:",
                isQueryLocked.current
              );
              return;
            }

            if (event.result.reason === sdk.ResultReason.RecognizedSpeech) {
              const recognizedText = event.result.text;
              if (recognizedText && recognizedText.trim()) {
                const cleanText = recognizedText.trim();

                if (shouldDropArabicUtterance(cleanText, event.result, language)) {
                  console.log("🚫 Dropping Arabic STT utterance:", {
                    text: cleanText,
                    confidence: getAzureUtteranceConfidence(event.result),
                  });
                  return;
                }
                const currentFinalText = finalTranscriptRef.current;

                let accumulatedText;
                if (currentFinalText) {
                  accumulatedText =
                    currentFinalText +
                    (currentFinalText.endsWith(" ") ? "" : " ") +
                    cleanText;
                } else {
                  accumulatedText = cleanText;
                }

                setFinalTranscription(accumulatedText);
                finalTranscriptRef.current = accumulatedText;

                console.log("📝 Accumulated transcription:", accumulatedText);

                // Clear previous timeout
                if (silenceTimeout.current) {
                  console.log(
                    "🔄 Clearing previous silence timeout in recognized"
                  );
                  clearTimeout(silenceTimeout.current);
                  silenceTimeout.current = null;
                }

                // Set NEW timeout for silence detection
                console.log("⏰ Setting 3-second silence timer");
                silenceTimeout.current = setTimeout(() => {
                  console.log(
                    "🔇 SILENCE DETECTED - 3 seconds passed, sending message"
                  );

                  if (
                    !isQueryLocked.current &&
                    !isDisabled &&
                    finalTranscriptRef.current.trim()
                  ) {
                    const readyText = finalTranscriptRef.current.trim();
                    if (
                      isArabicSpeechLanguage(language) &&
                      !looksLikeArabic(readyText)
                    ) {
                      console.log(
                        "🚫 Skipping Latin-only Arabic transcript:",
                        readyText
                      );
                      return;
                    }

                    console.log("📤 Sending transcript:", readyText);
                    lastMessageWasVoice.current = true;

                    // Disable mic after sending
                    setIsMicEnabled(false);
                    setIsRecording(false);

                    // Call parent callback - DON'T lock here, let parent handle it
                    handleQuery(readyText);

                    // Clean up resources
                    cleanupAllResources();
                  } else {
                    console.log(
                      "❌ Cannot send - locked:",
                      isQueryLocked.current,
                      "disabled:",
                      isDisabled
                    );
                  }
                }, 3000);
              }
            } else if (event.result.reason === sdk.ResultReason.NoMatch) {
              console.log("No speech could be recognized.");
            }
          };

          speechRecognizer.current.canceled = (s, e) => {
            console.error("Canceled event:", e);
          };
          speechRecognizer.current.sessionStopped = (s, e) => {
            console.log("Session stopped:", e);
          };

          // Start continuous recognition
          speechRecognizer.current.startContinuousRecognitionAsync(
            () => {
              console.log("✓ Speech recognition started");
              isCurrentlyProcessing.current = false;
              setIsMicLoading(false); // 🟢 stop loading animation
            },
            (err) => {
              console.error("Error starting recognition:", err);
              isCurrentlyProcessing.current = false;
              setIsMicLoading(false); // 🟢 stop loading animation
            }
          );
        } catch (error) {
          console.error("Error accessing microphone:", error);
          setIsMicEnabled(false);
          setIsRecording(false);
          await cleanupAllResources();
        }
      } else {
        // Stopping recording
        isCurrentlyProcessing.current = true;

        try {
          setIsMicEnabled(false);
          setIsRecording(false);
          await cleanupAllResources();
        } finally {
          isCurrentlyProcessing.current = false;
        }
      }
    },
    [isDisabled, cleanupAllResources, analyzeAudio, handleQuery, language]
  );

  // Handle auto-enable trigger
  useEffect(() => {
    if (shouldAutoEnable && handleMicButtonClick && !isMicEnabled) {
      handleMicButtonClick(false);
      setShouldAutoEnable(false);
    }
  }, [shouldAutoEnable, handleMicButtonClick, isMicEnabled]);

  const startAudioRecording = (stream) => {
    try {
      mediaRecorder.current = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorder.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setAudioChunks((prev) => [...prev, event.data]);
        }
      };

      mediaRecorder.current.onstop = () => {
        if (isMicEnabled && !isDisabled) {
          try {
            mediaRecorder.current.start();
          } catch (e) {
            console.warn("Error restarting MediaRecorder:", e);
          }
        }
      };

      mediaRecorder.current.start();
    } catch (error) {
      console.error("Error starting MediaRecorder:", error);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center gap-1 position-relative">
      {isMicEnabled ? (
        <button
          className={styles.bottomIcon}
          onClick={() => handleMicButtonClick(true)}
          disabled={isMicLoading}
        >
          <Mic size={20} color="#0E46A3" />
        </button>
      ) : (
        <button
          className={styles.bottomIcon}
          onClick={() => handleMicButtonClick(false)}
          disabled={isMicLoading}
        >
          <MicOff size={20} color="#666" />
        </button>
      )}

      {/* 🎯 Animated dots when loading */}
      {isMicLoading && !isMicEnabled && (
        <span
          className="loading-dots"
          style={{ marginLeft: "6px", color: "#0E46A3" }}
        >
          <span className="dot"></span>
          <span className="dot"></span>
          <span className="dot"></span>
        </span>
      )}

      {isMicEnabled && (
        <canvas
          style={{ transition: "opacity 0.3s ease-in-out" }}
          ref={canvasRef}
          height={30}
          width={50}
        />
      )}
    </div>
  );
};

export default SpeechToText;
